import '@bn-games/ui-shell/styles.css';
import { AiPlayer } from './ai/AiPlayer.js';
import { BoardEvaluator } from './ai/BoardEvaluator.js';
import { DifficultyStrategy } from './ai/DifficultyStrategy.js';
import { MinimaxSearch } from './ai/MinimaxSearch.js';
import { MoveEnumerator } from './ai/MoveEnumerator.js';
import { DrawRules } from './game/DrawRules.js';
import { GameState } from './game/GameState.js';
import { MoveValidator } from './game/MoveValidator.js';
import { getRuleSet } from './game/rules/index.js';
import type { RuleSet } from './game/rules/types.js';
import {
  opponentOf,
  sameSquare,
  type Color,
  type Move,
  type Square,
} from './game/types.js';
import { InputHandler } from './input/InputHandler.js';
import { MoveSelector } from './input/MoveSelector.js';
import {
  ConnectionManager,
  RoomClient,
  SessionStore,
  IDENTITY_ROOM_GRACE_WINDOW_MS,
  readRoomHash,
  resumeRoom,
  type ResumeOutcome,
  type ResumeFailureCause,
} from '@bn-games/room-client';
import { BN_AUTH_CHANGE, fetchMe, getAuthSnapshot, getWsTicket, isLoggedIn } from '@bn-games/auth-client';
import { NetworkGameController } from './network/NetworkGameController.js';
import { parseVariant, resolveSeatColor, SEATS, type ClientMessage, type GameSnapshot, type ServerMessage } from './network/protocol.js';
import { drawBoard } from './render/BoardRenderer.js';
import { canvasToSquare, squareToCanvasCenter, type ViewerRotation } from './render/geometry.js';
import { drawPiece, drawPieces } from './render/PieceRenderer.js';
import {
  frameAt,
  planMoveAnimation,
  type MoveAnimationFrame,
  type MoveAnimationPlan,
} from './render/PieceAnimator.js';
import { themeToRenderPalette, type RenderPalette } from './render/palette.js';
import { THEMES } from './settings/themes.js';
import {
  drawInteractionOverlay,
  drawKeyboardFocusRing,
  drawTurnBanner,
  type TurnBannerView,
} from './render/OverlayRenderer.js';
import { buildGameStateOverlayLabels } from './a11y/gameStateOverlayLabels.js';
import { KeyboardPlay } from './a11y/KeyboardPlay.js';
import { MoveAnnouncer, type AnnouncerMode, type CursorHint, type NarratedEvent } from './a11y/MoveAnnouncer.js';
import { CANVAS_SIZE } from './utils/constants.js';
import { LayoutManager } from './layout/LayoutManager.js';
import type { LayoutConfig } from './layout/types.js';
import { SettingsManager } from './settings/SettingsManager.js';
import { playPiecePlace, playGameOver, playChime, setMuted, TurnSignal } from '@bn-games/audio-core';
import { drawPauseGear, gearHitTest, prefersReducedMotion, type GearIconBounds } from '@bn-games/render-helpers';
import { enableLogger, dumpLogText } from '@bn-games/debug-core';
import type { Settings, Variant } from './settings/types.js';
import { initI18n, setLocale, t } from './i18n/I18n.js';
import { registerServiceWorker } from './pwa.js';
import { CanvasTooltip, MenuRouter, createMainMenuScreen, setupMenuScale, GameOverlay, AfkTimer, PingButton, createGameStateOverlay, announce, openModal, openResumeFailureModal, type GameStateOverlayHandle, type GameStateOverlaySnapshot, type GameStateOverlayButtonId, type GameOverlayContext, type ModalHandle, type ModalAction } from '@bn-games/ui-shell';
import { createDiscardModal } from '@bn-games/history-core/ui';
import { initDiscordActivity, isDiscordActivity } from '@bn-games/discord-activity';
import type { MenuContext, Opponent, Scene } from './ui/types.js';
import { createPlayMenuScreen } from './ui/screens/PlayMenuScreen.js';
import { createSettingsScreen } from './ui/screens/SettingsScreen.js';
import { createRulesScreen } from './ui/screens/RulesScreen.js';
import { createAboutScreen } from './ui/screens/AboutScreen.js';
import { createOnlineCreateScreen } from './ui/screens/OnlineCreateScreen.js';
import { createOnlineJoinScreen } from './ui/screens/OnlineJoinScreen.js';
import { createHistoryListScreen } from './ui/screens/HistoryListScreen.js';
import { buildInGameSettingsRows } from './ui/inGameSettings.js';
import { mountSocialDock } from './ui/socialDock.js';
import { PendingSyncStore, commitFinishedRecord } from '@bn-games/history-core';
import { ActiveGameStore } from './history/ActiveGameStore.js';
import { GameRecorder } from './history/GameRecorder.js';
import { HistoryStore } from './history/HistoryStore.js';
import { ReplayScene } from './history/ReplayScene.js';
import { syncOnlineRecord } from './history/serverSync.js';
import { recordFromActiveSession } from './history/types.js';
import type {
  ActiveSession,
  GameRecord,
  RecordedMode,
  RecordedMove,
  RecordedSeed,
} from './history/types.js';

function setupCanvas(): { ctx: CanvasRenderingContext2D; canvas: HTMLCanvasElement } {
  const canvas = document.createElement('canvas');
  // Keyboard-accessible canvas — actual ARIA label is set after i18n boots.
  canvas.setAttribute('tabindex', '0');
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Failed to acquire 2D canvas context');
  return { ctx, canvas };
}

/**
 * Applies layout-driven sizing to the canvas. The drawing code uses a fixed
 * `CANVAS_SIZE` coordinate system; this function resizes the backing store to
 * `displaySize * devicePixelRatio` and sets a transform so 1 logical unit maps
 * to the right number of device pixels. Without this the bitmap would stretch
 * non-uniformly on mobile and turn circles into ellipses.
 */
function applyCanvasLayout(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  layout: LayoutConfig,
): void {
  const cssSize = layout.canvasDisplaySize;
  const bitmapSize = Math.max(1, Math.round(cssSize * layout.devicePixelRatio));
  if (canvas.width !== bitmapSize) canvas.width = bitmapSize;
  if (canvas.height !== bitmapSize) canvas.height = bitmapSize;
  canvas.style.width = `${cssSize}px`;
  canvas.style.height = `${cssSize}px`;
  const scale = bitmapSize / CANVAS_SIZE;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
}

type Mode = 'menu' | 'game';

function modeForScene(scene: Scene): Mode {
  // 'replay' shares the canvas with 'game'; the renderer just swaps.
  return scene === 'game' || scene === 'replay' ? 'game' : 'menu';
}

type GameStatus =
  | { readonly kind: 'playing'; readonly toMove: Color }
  | { readonly kind: 'win'; readonly winner: Color }
  | { readonly kind: 'draw' };

function evaluateStatus(
  state: GameState,
  validator: MoveValidator,
  drawRules: DrawRules,
): GameStatus {
  if (drawRules.detect(state)) return { kind: 'draw' };
  if (!validator.hasAnyMove(state)) {
    return { kind: 'win', winner: opponentOf(state.currentPlayer) };
  }
  return { kind: 'playing', toMove: state.currentPlayer };
}

function bannerFor(status: GameStatus): TurnBannerView {
  switch (status.kind) {
    case 'playing':
      return { kind: 'turn', player: status.toMove };
    case 'win':
      return { kind: 'win', winner: status.winner };
    case 'draw':
      return { kind: 'draw' };
  }
}

interface AnimationContext {
  readonly plan: MoveAnimationPlan;
  readonly startMs: number;
  readonly preMoveState: GameState;
  readonly move: Move;
  readonly color: Color;
}

const DEFAULT_LOCAL_COLOR: Color = 'white';
const AI_THINK_DELAY_MS = 320;

// M7c outbox — a finished ONLINE record for a signed-in player is queued
// here and flushed to the server; it never touches `historyStore`. See
// commitRecord() below.
const outbox = new PendingSyncStore<GameRecord>({ storageKey: 'checkers-pending-sync' });

function snapshotState(state: GameState): GameSnapshot {
  const pieces: Array<{ row: number; col: number; color: Color; kind: 'man' | 'king' }> = [];
  for (const { square, piece } of state.pieces()) {
    pieces.push({ row: square.row, col: square.col, color: piece.color, kind: piece.kind });
  }
  return {
    pieces,
    currentPlayer: state.currentPlayer,
    moveCount: state.moveCount,
    quietMoveCount: state.quietMoveCount,
  };
}

function restoreState(snapshot: GameSnapshot, ruleSet: RuleSet): GameState {
  const pieces = snapshot.pieces.map((p) => ({
    square: { row: p.row, col: p.col },
    piece: { color: p.color, kind: p.kind },
  }));
  return GameState.custom(ruleSet, pieces, snapshot.currentPlayer, {
    moveCount: snapshot.moveCount,
    quietMoveCount: snapshot.quietMoveCount,
  });
}

async function main(): Promise<void> {
  if (new URLSearchParams(window.location.search).has('log')) {
    enableLogger();
    (window as unknown as Record<string, unknown>)['dumpLog'] = dumpLogText;
  }

  const settingsManager = new SettingsManager();
  await initI18n(settingsManager.get().language);
  // The activity iframe is never installable and its build ships no service
  // worker, so registration would only be a no-op round trip.
  if (!isDiscordActivity()) registerServiceWorker();
  setupMenuScale();

  applyTheme(settingsManager.get().theme);
  setMuted(!settingsManager.get().sound);

  // "Your turn" cue. Reads the setting per edge so the Settings toggle takes
  // effect without a restart; `setMuted` above still gates the sound itself.
  const turnSignal = new TurnSignal({
    isEnabled: () => settingsManager.get().turnStartSound,
  });

  const { ctx, canvas } = setupCanvas();
  canvas.setAttribute('aria-label', t('a11y.canvasLabel'));
  const layoutManager = new LayoutManager();
  applyCanvasLayout(canvas, ctx, layoutManager.getConfig());
  applyLayoutAttributes(layoutManager.getConfig());
  layoutManager.onChange((config) => {
    applyCanvasLayout(canvas, ctx, config);
    applyLayoutAttributes(config);
  });
  let palette: RenderPalette = themeToRenderPalette(THEMES[settingsManager.get().theme]);

  let ruleSet: RuleSet = getRuleSet(settingsManager.get().variant);
  let validator = new MoveValidator(ruleSet);
  let drawRules = new DrawRules(ruleSet);
  let enumerator = new MoveEnumerator(validator);
  let evaluator = new BoardEvaluator(ruleSet, validator);
  let search = new MinimaxSearch(evaluator, enumerator, drawRules, ruleSet.boardSize);
  let strategy = new DifficultyStrategy({ enumerator, evaluator, search });

  function rebindEngineFor(nextRuleSet: RuleSet): void {
    ruleSet = nextRuleSet;
    validator = new MoveValidator(ruleSet);
    drawRules = new DrawRules(ruleSet);
    enumerator = new MoveEnumerator(validator);
    evaluator = new BoardEvaluator(ruleSet, validator);
    search = new MinimaxSearch(evaluator, enumerator, drawRules, ruleSet.boardSize);
    strategy = new DifficultyStrategy({ enumerator, evaluator, search });
    aiPlayer = new AiPlayer({
      color: aiColor(),
      strategy,
      minThinkMs: AI_THINK_DELAY_MS,
      onMove: (move) => beginAnimation(move),
    });
    selector = buildSelector(state);
  }

  let state = GameState.initial(ruleSet);
  let status: GameStatus = evaluateStatus(state, validator, drawRules);
  let animation: AnimationContext | null = null;
  let hoveredFallback: Square | null = null;
  let opponent: Opponent = 'ai';
  let localColor: Color = DEFAULT_LOCAL_COLOR;
  // The side the host wants when creating an online room. Local vs-AI color
  // is derived from settings.defaultColor on each match start.
  let pendingOnlineColor: Color = DEFAULT_LOCAL_COLOR;
  // Share-link prefill for the manual Join screen. No longer populated from
  // #room=CODE links — those now resolve straight through attemptResume()
  // (see resumeFromHashOrSession) instead of dropping the user on this
  // screen. Left in place (dead after this change) — see T5 summary.
  let pendingJoinCode: string | null = null;
  let pendingRemoteMove: Move | null = null;
  let endNotifiedThisGame = false;
  // DOM modal for the cross-game state overlay (online disconnect / rematch
  // flow + vs-AI game-over). Replaces the canvas draw + pointer hit-test so
  // the modal buttons are keyboard- and screen-reader-reachable. The handle
  // is constructed lazily on first relevant state and torn down by
  // syncGameStateOverlay when the state clears.
  let gameStateOverlay: GameStateOverlayHandle | null = null;
  // Label cache for the game-state overlay. The render loop ticks at RAF
  // rate, so rebuilding the 7-callback label object (with eager `t()` calls
  // for the rematch-preview subtitle + button labels) on every frame is real
  // garbage churn. Invalidated when the language changes or when the upcoming
  // rematch color flips — both rare events.
  let gameStateLabelsCache:
    | { labels: ReturnType<typeof buildGameStateOverlayLabels>; upcoming: Color | null }
    | null = null;
  function invalidateGameStateLabels(): void {
    gameStateLabelsCache = null;
  }
  function getGameStateLabels(): ReturnType<typeof buildGameStateOverlayLabels> {
    const upcoming = opponentOf(localColor);
    if (gameStateLabelsCache === null || gameStateLabelsCache.upcoming !== upcoming) {
      gameStateLabelsCache = { labels: buildGameStateOverlayLabels(upcoming), upcoming };
    }
    return gameStateLabelsCache.labels;
  }
  // Variant declared by the room for the current online game. The host sets
  // it when calling createRoom; the joiner / reconnecting peer reads it off
  // the lobby envelope. Cleared whenever we leave the online flow.
  let onlineVariant: Variant | null = null;
  // Set by resumeActiveGame() right before navigateTo('game'); navigateTo
  // reads + clears it so startNewGame is skipped when restoring state.
  let resumingActiveGame = false;
  // ─── In-game overlay (gear modal) + AFK hint state ─────────────────
  let gameOverlay: GameOverlay | null = null;
  let afkTimer: AfkTimer | null = null;
  let gearBounds: GearIconBounds | null = null;
  let gearHovered = false;
  const gearTooltip = new CanvasTooltip(canvas);
  let afkHintSquare: Square | null = null;
  let afkHintExpiresAt = 0;
  // Transient flag for the game-over banner — currently unread by checkers
  // UI but kept to mirror backgammon's shape and reserve room for a future
  // "by resignation" label hook.
  let lastEndedByResign: 'local' | 'opponent' | null = null;
  void lastEndedByResign;
  let escapeKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  // ─── AFK ping (online only) ─────────────────────────────────────────
  let pingButton: PingButton | null = null;
  let pingButtonContainer: HTMLDivElement | null = null;
  let unsubAfkTracker: (() => void) | null = null;

  function rotationFor(): ViewerRotation {
    // Local hotseat keeps the canonical orientation (white at bottom) — every
    // other mode rotates so the player's pieces sit at the bottom.
    if (opponent === 'local') return 0;
    return localColor === 'black' ? 180 : 0;
  }

  function aiColor(): Color {
    return opponentOf(localColor);
  }

  const envWsUrl = import.meta.env['VITE_WS_SERVER_URL'] as string | undefined;
  const wsServerUrl = envWsUrl ?? 'ws://localhost:3001/ws/checkers';
  const envLobbyWsUrl = import.meta.env['VITE_LOBBY_WS_URL'] as string | undefined;
  const sessionStore = new SessionStore('checkers.online.session', IDENTITY_ROOM_GRACE_WINDOW_MS);
  const connection = new ConnectionManager<ClientMessage, ServerMessage>({ serverUrl: wsServerUrl });
  // Resolve the WS ticket at connect time, never at screen-mount time: the
  // ticket's TTL is 60s, and the online create/join screens routinely sit open
  // longer than that while the player picks a side or waits for a friend's
  // code. A stale ticket fails verifyWsTicket, the upgrade is accepted as
  // anonymous, and the seat's identity is bound to null for the life of the
  // room — silently costing a signed-in player their verified name, presence,
  // cross-device resume and history association.
  // Discord is deliberately excluded: its ticket comes from the activity
  // handshake and cannot be re-minted from the web session, so that path keeps
  // setTicket() (which the provider would otherwise win over).
  if (!isDiscordActivity()) connection.setTicketProvider(getWsTicket);
  // Resolve the signed-in state once at boot. Two paths reach a live room
  // without ever passing the main menu — the Discord activity bootstrap and
  // hash/session auto-resume — and neither mounts the AuthWidget that is
  // otherwise the only caller of fetchMe(). Without this, isLoggedIn() stays
  // false on those paths and serverSync silently skips recording the match.
  // Chained onto the outbox flush below: app start is the one point where a
  // record queued last session (signed in, POST never confirmed) gets
  // retried, and that needs a resolved auth answer first.
  void fetchMe().then(() => flushOutboxIfSignedIn());
  const roomClient = new RoomClient<ClientMessage, ServerMessage>(connection, sessionStore);
  let network: NetworkGameController | null = null;
  let currentScene: Scene = 'menu';
  const socialDock = mountSocialDock({
    serverUrl: envLobbyWsUrl ?? 'ws://localhost:3001/ws/lobby',
    isGameInProgress: () => currentScene === 'game' && status.kind === 'playing',
  });

  const historyStore = new HistoryStore();
  const recorder = new GameRecorder();

  /**
   * M7c record routing: a signed-in player's finished ONLINE match goes to
   * the outbox + server only, never to `historyStore`; everything else
   * (offline modes, or an anonymous player's online match) is a device
   * record. `isLoggedIn()`'s sticky answer isn't enough here — the outbox
   * needs an account id to stamp the entry with, so this reads the full
   * snapshot instead.
   */
  function commitRecord(record: GameRecord): void {
    const { authState, userId } = getAuthSnapshot();
    commitFinishedRecord({
      record,
      accountId: userId ?? '',
      isOnlineMatch: record.seed.mode === 'online',
      signedIn: authState === 'signed-in',
      saveLocal: (r) => historyStore.add(r),
      syncRemote: syncOnlineRecord,
      outbox,
    });
  }

  /** Retries any queued outbox entries for the current account. No-op when
   * signed out or when the auth answer hasn't resolved yet ('unknown'). */
  function flushOutboxIfSignedIn(): void {
    const { authState, userId } = getAuthSnapshot();
    if (authState !== 'signed-in' || userId === null) return;
    void outbox.flush(userId, syncOnlineRecord);
  }
  // i18next's `t` carries a typed key-literal union; the announcer's translator
  // is generic so the unit tests can stub it. Cast at the boundary.
  const tForAnnouncer = (key: string, vars?: Record<string, string | number>): string =>
    t(key as never, vars as never) as unknown as string;
  function announcerMode(): AnnouncerMode {
    if (opponent === 'ai') return 'ai';
    if (opponent === 'online') return 'online';
    return 'local';
  }
  const moveAnnouncer = new MoveAnnouncer(tForAnnouncer, {
    getMode: () => announcerMode(),
    getLocalColor: () => (opponent === 'local' ? null : localColor),
    getLocalName: () => localDisplayName() || undefined,
    getOpponentName: () =>
      network?.opponentName ?? (opponent === 'ai' ? 'Bot' : undefined),
    getVariant: () => settingsManager.get().variant,
  });
  function fireNarration(event: NarratedEvent | null): void {
    if (event === null) return;
    announce(event.text, event.assertive === true ? { assertive: true } : {});
  }
  let pendingReplay: { record: GameRecord; returnTo: 'history' | 'menu' } | null = null;
  const replayScene = new ReplayScene({
    requestRender: () => {}, // rAF loop drives rendering already
    onExit: (target) => {
      navigateTo(target === 'history' ? 'history' : 'menu');
    },
    getRotation: () => 0,
  });

  let aiPlayer = new AiPlayer({
    color: aiColor(),
    strategy,
    minThinkMs: AI_THINK_DELAY_MS,
    onMove: (move) => beginAnimation(move),
  });

  function isAiTurn(): boolean {
    return opponent === 'ai' && state.currentPlayer === aiPlayer.color;
  }

  function isRemoteTurn(): boolean {
    return opponent === 'online' && state.currentPlayer !== localColor;
  }

  function buildSelector(initial: GameState): MoveSelector {
    return new MoveSelector(initial, validator, {
      getState: () => state,
      getRotation: () => rotationFor(),
      isInputBlocked: () =>
        animation !== null ||
        status.kind !== 'playing' ||
        isAiTurn() ||
        isRemoteTurn() ||
        (network !== null && network.overlay !== 'none'),
      onMove: (move) => {
        if (opponent === 'online' && network) {
          network.notifyLocalMove(move);
        }
        beginAnimation(move);
      },
    });
  }

  let selector = buildSelector(state);

  function beginAnimation(move: Move): void {
    const mover = state.pieceAt(move.from);
    if (!mover) return;
    const ctx: AnimationContext = {
      plan: planMoveAnimation(move, mover),
      startMs: performance.now(),
      preMoveState: state,
      move,
      color: mover.color,
    };
    // Reduced motion: skip the rAF tween + capture fade + promotion flash and
    // settle straight to the post-move state. finishAnimation handles record,
    // narration, sound, and turn-handoff identically — the only difference is
    // that the piece teleports rather than slides.
    if (prefersReducedMotion()) {
      finishAnimation(ctx);
      return;
    }
    animation = ctx;
  }

  function finishAnimation(anim: AnimationContext): void {
    recorder.recordMove(anim.move, anim.color);
    // Read the moving piece's pre-move kind so the narration can say "man"
    // vs "king" before any mid-chain promotion happens.
    const moverPiece = anim.preMoveState.pieceAt(anim.move.from);
    const moverKindBefore = moverPiece?.kind ?? 'man';
    state = state.applyMove(anim.move);
    status = evaluateStatus(state, validator, drawRules);
    selector.resetTo(state);
    animation = null;
    playPiecePlace();
    // Combine move + (turn-change | game-over) into a single announce so the
    // SR hears both — back-to-back announces clobber the polite live region.
    const moveText = moveAnnouncer.describeMove(anim.move, anim.color, moverKindBefore).text;
    if (status.kind !== 'playing') {
      playGameOver();
      finalizeRecord(status);
      const overEvent = moveAnnouncer.describeGameOver(
        status.kind === 'draw'
          ? { kind: 'draw' }
          : { kind: 'win', winner: status.winner },
      );
      announce(`${moveText} ${overEvent.text}`, { assertive: true });
    } else {
      const turnEvent = moveAnnouncer.describeTurnStart(state.currentPlayer);
      announce(turnEvent ? `${moveText} ${turnEvent.text}` : moveText);
    }
    notifyTurnOwnershipChanged();
    handleAfterMove();
  }

  function finalizeRecord(finalStatus: GameStatus): void {
    if (!recorder.getSession()) return;
    if (finalStatus.kind === 'playing') return;
    const result: 'win' | 'draw' = finalStatus.kind === 'win' ? 'win' : 'draw';
    const winner = finalStatus.kind === 'win' ? finalStatus.winner : null;
    const record = recorder.finish(result, winner);
    commitRecord(record);
    recorder.discard();
  }

  function handleAfterMove(): void {
    if (status.kind !== 'playing') {
      afkTimer?.disarm();
      afkHintSquare = null;
      maybeNotifyGameOver();
      return;
    }
    if (pendingRemoteMove) {
      const next = pendingRemoteMove;
      pendingRemoteMove = null;
      beginAnimation(next);
      return;
    }
    maybeRequestAiMove();
    setupAfkForCurrentPhase();
  }

  function maybeNotifyGameOver(): void {
    if (opponent !== 'online' || !network || endNotifiedThisGame) return;
    endNotifiedThisGame = true;
    if (status.kind === 'win') {
      const localWon = status.winner === localColor;
      network.showGameOver(localWon ? 'win' : 'loss');
    } else if (status.kind === 'draw') {
      network.showGameOver('draw');
    }
    // Invalidate auto-reconnect once the match is over. Otherwise a later
    // tab reload auto-rejoins the still-alive room, the server asks the
    // survivor for state, and the rejoiner adopts a stale GAME_OVER
    // snapshot ("previous match" leak).
    sessionStore.clear();
  }

  function maybeRequestAiMove(): void {
    if (status.kind !== 'playing') return;
    if (!isAiTurn()) return;
    aiPlayer.setDifficulty(settingsManager.get().difficulty);
    aiPlayer.notifyTurn(state);
  }

  function startNewGame(): void {
    aiPlayer.cancel();
    const activeVariant: Variant =
      opponent === 'online' && onlineVariant !== null
        ? onlineVariant
        : settingsManager.get().variant;
    rebindEngineFor(getRuleSet(activeVariant));
    search.reset();
    state = GameState.initial(ruleSet);
    status = evaluateStatus(state, validator, drawRules);
    selector.resetTo(state);
    animation = null;
    hoveredFallback = null;
    pendingRemoteMove = null;
    endNotifiedThisGame = false;
    // A new ActiveSession replaces any abandoned one in localStorage.
    recorder.discard();
    recorder.start(buildRecorderSeed());
    moveAnnouncer.resetTurnMemory();
    fireNarration(moveAnnouncer.describeTurnStart(state.currentPlayer));
    maybeRequestAiMove();
    setupAfkForCurrentPhase();
    // Before the funnel below: a new game that opens on our colour should
    // sound, and without the reset it reads as "already our turn".
    turnSignal.reset();
    notifyTurnOwnershipChanged();
  }

  function rebuildStateFromMoves(moves: readonly RecordedMove[]): GameState {
    let s = GameState.initial(ruleSet);
    for (const m of moves) {
      s = s.applyMove({
        from: m.from,
        to: m.to,
        path: m.path,
        captures: m.captures,
        promoted: m.promoted,
      });
    }
    return s;
  }

  function resumeActiveGame(): void {
    const session = recorder.getSession() ?? ActiveGameStore.peek();
    if (!session) return;
    const mode = session.seed.mode;

    if (mode === 'online') {
      handleOnlineResume(session);
      return;
    }

    aiPlayer.cancel();
    rebindEngineFor(getRuleSet(session.seed.variant));
    search.reset();
    recorder.adopt(session);
    state = rebuildStateFromMoves(session.moves);
    status = evaluateStatus(state, validator, drawRules);
    selector.resetTo(state);
    animation = null;
    hoveredFallback = null;
    pendingRemoteMove = null;
    endNotifiedThisGame = false;
    opponent = session.seed.mode === 'ai' ? 'ai' : 'local';
    localColor = 'white';

    resumingActiveGame = true;
    navigateTo('game');
    // Trigger AI move if it's the AI's turn after resume.
    maybeRequestAiMove();
  }

  /**
   * Opens the shared "Reconnecting…" progress modal, drives the T2
   * `resumeRoom()` flow helper to completion, and on failure hands off to
   * `showResumeFailure`. Shared by every resume entry point (menu, boot
   * hash/session, active-session button) so they all get the same feedback
   * instead of the boot path resolving silently on the main menu.
   */
  function attemptResume(roomCode: string, session: ActiveSession | null): void {
    const modal = openModal({
      title: t('online.resumeModal.reconnectingTitle'),
      body: t('online.resumeModal.reconnectingBody'),
      actions: [],
      dismissible: false,
    });

    void resumeRoom(roomClient, {
      roomCode,
      playerName: settingsManager.get().playerName,
    }).then((outcome: ResumeOutcome) => {
      modal.close();
      // "ready" navigation is already handled by the top-level
      // roomClient.onUpdate listener; "waiting" is a sub-frame transient for
      // this 2-seat game, so the progress modal being closed on "ready" is
      // the only follow-up needed here.
      if (outcome.kind === 'failed') {
        showResumeFailure(outcome.cause, session, () => attemptResume(roomCode, session));
      }
    });
  }

  function showResumeFailure(
    cause: ResumeFailureCause,
    session: ActiveSession | null,
    onRetry: () => void,
  ): void {
    let modal: ModalHandle;
    const extraActions: ModalAction[] = [];
    if (session) {
      extraActions.push({
        label: t('online.resumeModal.viewSaved'),
        onClick: () => {
          modal.close();
          const record = recordFromActiveSession(session);
          pendingReplay = { record, returnTo: 'menu' };
          navigateTo('replay');
        },
      });
      extraActions.push({
        label: t('online.resumeModal.discard'),
        onClick: () => {
          modal.close();
          discardActiveSession(() => {
            if (router.getCurrentScene() === 'play') {
              router.hide();
              router.show('play');
            }
          });
        },
      });
    }
    modal = openResumeFailureModal({
      cause,
      signedIn: isLoggedIn(),
      onRetry,
      // Sign-in doesn't reclaim this seat — resolveSeat fixes identity at
      // first-seat time — but it should still pick up a freshly-signed-in
      // state for the next attempt, the same refresh fetchMe() does at boot.
      onSignIn: () => { void fetchMe(); },
      extraActions,
    });
  }

  function handleOnlineResume(session: ActiveSession): void {
    const seed = session.seed;
    if (!seed.roomCode) {
      // No server round-trip to fail — there's genuinely no room to rejoin,
      // which is the most honest of the four causes.
      showResumeFailure('ROOM_GONE', session, () => handleOnlineResume(session));
      return;
    }

    // Adopt up-front so the seed/id survive across the eventual STATE_SYNC.
    recorder.adopt(session);
    attemptResume(seed.roomCode, session);
  }

  function discardActiveSession(onConfirm?: () => void): void {
    const session = recorder.getSession() ?? ActiveGameStore.peek();
    if (!session) {
      onConfirm?.();
      return;
    }
    createDiscardModal({
      strings: {
        title: t('playMenu.discardModal.title'),
        body: t('playMenu.discardModal.body'),
        keep: t('playMenu.discardModal.keep'),
        discard: t('playMenu.discardModal.discard'),
      },
      onDiscard: () => {
        recorder.discard();
        onConfirm?.();
      },
      dismissible: true,
    });
  }

  /**
   * The local seat's own display name. When the server has verified this
   * seat (a signed-in player), the roster carries the handle — prefer that
   * over the raw settings value so the local player is labelled the same
   * way opponents see them.
   */
  function localDisplayName(): string {
    const info = roomClient.getInfo();
    const rosterName = info.players?.find((p) => p.seat === info.localSeat)?.name;
    return rosterName ?? settingsManager.get().playerName;
  }

  function buildRecorderSeed(): RecordedSeed {
    const settings = settingsManager.get();
    const mode: RecordedMode =
      opponent === 'ai' ? 'ai' : opponent === 'online' ? 'online' : 'local';
    const localName = localDisplayName().trim() || 'Player';
    const remoteName =
      network?.opponentName ??
      (opponent === 'ai'
        ? `AI (${settings.difficulty})`
        : t('common.opponent'));
    let playerWhite: string;
    let playerBlack: string;
    if (opponent === 'online') {
      playerWhite = localColor === 'white' ? localName : remoteName;
      playerBlack = localColor === 'white' ? remoteName : localName;
    } else if (opponent === 'ai') {
      playerWhite = localColor === 'white' ? localName : remoteName;
      playerBlack = localColor === 'white' ? remoteName : localName;
    } else {
      playerWhite = t('common.white');
      playerBlack = t('common.black');
    }
    return {
      mode,
      variant: settings.variant,
      difficulty: mode === 'ai' ? settings.difficulty : null,
      playerWhite,
      playerBlack,
      localColor: opponent === 'local' ? null : localColor,
      roomCode: null,
      createdAt: Date.now(),
    };
  }

  function adoptSnapshot(snapshot: GameSnapshot): void {
    aiPlayer.cancel();
    state = restoreState(snapshot, ruleSet);
    status = evaluateStatus(state, validator, drawRules);
    selector.resetTo(state);
    animation = null;
    pendingRemoteMove = null;
    endNotifiedThisGame = false;
    // STATE_SYNC replaces the board wholesale — the local move trace is stale.
    recorder.resetTrace();
    notifyTurnOwnershipChanged();
  }

  // ─── In-game overlay (gear modal) + AFK assist ─────────────────────────────

  function shouldArmAfk(): boolean {
    const s = settingsManager.get();
    if (!s.afkAssistEnabled) return false;
    if (currentScene !== 'game') return false;
    if (status.kind !== 'playing') return false;
    if (animation !== null) return false;
    // Local turn: vs-AI/online wait for our color; local hotseat treats every
    // turn as local.
    if (opponent === 'ai' || opponent === 'online') {
      return state.currentPlayer === localColor;
    }
    return true;
  }

  function fireAfkHint(): void {
    if (!shouldArmAfk()) return;
    const moves = enumerator.enumerate(state);
    const first = moves[0];
    if (!first) return;
    afkHintSquare = first.from;
    afkHintExpiresAt = Date.now() + 1600;
    playChime();
    setTimeout(() => {
      if (Date.now() >= afkHintExpiresAt) {
        afkHintSquare = null;
      }
    }, 1600);
  }

  function pokeAfk(): void {
    afkHintSquare = null;
    afkTimer?.poke();
  }

  function setupAfkForCurrentPhase(): void {
    if (afkTimer === null) return;
    if (shouldArmAfk()) afkTimer.arm();
    else {
      afkTimer.disarm();
      afkHintSquare = null;
    }
  }

  function buildOverlayContext(): GameOverlayContext {
    return {
      getSettingsRows: () =>
        buildInGameSettingsRows({
          ctx: {
            getSettings: () => settingsManager.get(),
            updateSettings: ctxMenu.updateSettings,
          },
          isOnline: () => network !== null,
          restartMatchOffline: () => {
            restartCurrentMatchOffline();
            gameOverlay?.refreshSettings();
          },
        }),
      requestRestart: () => {
        if (network) {
          network.requestRestart();
        } else {
          restartCurrentMatchOffline();
          gameOverlay?.close();
        }
      },
      acceptRestart: () => network?.acceptRestart(),
      declineRestart: () => network?.declineRestart(),
      requestResign: () => {
        if (network) {
          network.requestResign();
        } else {
          resolveResignLocally('local');
        }
      },
      exitToMainMenu: () => navigateTo('menu'),
      isOnline: () => network !== null,
      getRestartState: () => ({
        pending: network?.isRestartPending() ?? false,
        canPropose: network?.canLocalProposeRestart() ?? true,
      }),
    };
  }

  function ensureOverlayInstance(): void {
    if (gameOverlay !== null) return;
    gameOverlay = new GameOverlay({
      context: buildOverlayContext(),
      strings: {
        title: t('gameOverlay.title'),
        settingsLabel: t('gameOverlay.settingsLabel'),
        settingsBackLabel: t('gameOverlay.settingsBackLabel'),
        settingsViewTitle: t('gameOverlay.settingsViewTitle'),
        restartLabel: t('gameOverlay.restartLabel'),
        restartHintOnline: t('gameOverlay.restartHintOnline'),
        restartHintAlreadyProposed: t('gameOverlay.restartHintAlreadyProposed'),
        restartHintPending: t('gameOverlay.restartHintPending'),
        returnToMenuLabel: t('gameOverlay.returnToMenuLabel'),
        returnToMenuConfirmTitle: t('gameOverlay.returnToMenuConfirmTitle'),
        returnToMenuConfirmYes: t('gameOverlay.returnToMenuConfirmYes'),
        returnToMenuConfirmNo: t('gameOverlay.returnToMenuConfirmNo'),
        giveUpLabel: t('gameOverlay.giveUpLabel'),
        giveUpConfirmTitle: t('gameOverlay.giveUpConfirmTitle'),
        giveUpConfirmYes: t('gameOverlay.giveUpConfirmYes'),
        giveUpConfirmNo: t('gameOverlay.giveUpConfirmNo'),
        close: t('gameOverlay.close'),
        settingsTabs: {
          gameplay: t('gameOverlay.settingsTabs.gameplay'),
          interface: t('gameOverlay.settingsTabs.interface'),
          audio: t('gameOverlay.settingsTabs.audio'),
        },
      },
    });
  }

  function toggleGameOverlay(): void {
    ensureOverlayInstance();
    gameOverlay?.toggle();
  }

  function mountGameSceneOverlay(): void {
    ensureOverlayInstance();
    if (afkTimer === null) {
      afkTimer = new AfkTimer(
        {
          isLocalTurn: () => shouldArmAfk(),
          onTimeout: () => fireAfkHint(),
        },
        settingsManager.get().afkTimeoutMs,
      );
    }
    if (escapeKeyHandler === null) {
      escapeKeyHandler = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        if (currentScene !== 'game') return;
        e.preventDefault();
        e.stopPropagation();
        toggleGameOverlay();
      };
      // Capture phase so the global Escape→navigate listener below doesn't fire.
      window.addEventListener('keydown', escapeKeyHandler, true);
    }
    setupAfkForCurrentPhase();
    wireAfkPingButton();
    notifyTurnOwnershipChanged();
  }

  function unmountGameSceneOverlay(): void {
    if (gameOverlay?.isOpen()) gameOverlay.close();
    gameStateOverlay?.close();
    gameStateOverlay = null;
    afkTimer?.disarm();
    afkTimer = null;
    afkHintSquare = null;
    gearBounds = null;
    gearTooltip.set(null);
    if (escapeKeyHandler !== null) {
      window.removeEventListener('keydown', escapeKeyHandler, true);
      escapeKeyHandler = null;
    }
    teardownAfkPingButton();
  }

  function wireAfkPingButton(): void {
    if (network === null) return;
    if (pingButton !== null) return;
    pingButtonContainer = document.createElement('div');
    pingButtonContainer.style.position = 'fixed';
    pingButtonContainer.style.right = '1rem';
    pingButtonContainer.style.bottom = '1rem';
    pingButtonContainer.style.zIndex = '50';
    document.body.appendChild(pingButtonContainer);
    const button = new PingButton({
      label: '🔔',
      title: t('game.pingOpponent'),
      ariaLabel: t('game.pingOpponent'),
    });
    button.mount(pingButtonContainer);
    const tracker = network.getAfkPingTracker();
    const updateVisibility = (): void => {
      button.setVisible(tracker.canPing() && settingsManager.get().showAfkPingButton);
    };
    unsubAfkTracker = tracker.subscribe(updateVisibility);
    button.setOnClick(() => {
      network?.sendAfkPing();
      updateVisibility();
    });
    updateVisibility();
    pingButton = button;
  }

  function teardownAfkPingButton(): void {
    if (unsubAfkTracker !== null) {
      unsubAfkTracker();
      unsubAfkTracker = null;
    }
    pingButton?.dispose();
    pingButton = null;
    if (pingButtonContainer !== null) {
      pingButtonContainer.remove();
      pingButtonContainer = null;
    }
  }

  /**
   * Single funnel for "turn ownership may have changed" — called after every
   * move, every STATE_SYNC, on new game, and on scene mount. Drives the online
   * AFK tracker and the your-turn cue; both dedupe internally, so callers
   * don't have to know whether anything actually moved.
   */
  function notifyTurnOwnershipChanged(): void {
    network?.notifyTurnState(isRemoteTurn());
    turnSignal.update(isLocalTurnForSignal());
  }

  /**
   * Hot-seat is excluded: the human being told "your turn" is the one who just
   * moved, sitting at the same device. The cue is for turns that arrive while
   * someone *else* was acting — the AI thinking, or a remote seat.
   */
  function isLocalTurnForSignal(): boolean {
    if (opponent === 'local') return false;
    if (status.kind !== 'playing') return false;
    return state.currentPlayer === localColor;
  }

  function restartCurrentMatchOffline(): void {
    ActiveGameStore.clear();
    lastEndedByResign = null;
    startNewGame();
  }

  function resolveResignLocally(side: 'local' | 'opponent'): void {
    const resignerColor: Color =
      side === 'local' ? localColor : opponentOf(localColor);
    const winnerColor: Color = opponentOf(resignerColor);
    lastEndedByResign = side;
    status = { kind: 'win', winner: winnerColor };
    aiPlayer.cancel();
    selector.resetTo(state);
    if (recorder.getSession()) {
      const record = recorder.finish('win', winnerColor);
      commitRecord(record);
      recorder.discard();
    }
    if (network && side === 'local') {
      // Server already broadcasts the resign; let the existing game-over flow run.
    } else if (network && side === 'opponent') {
      // Same — server flow.
    }
    gameOverlay?.close();
    if (network) {
      const localWon = winnerColor === localColor;
      network.showGameOver(localWon ? 'win' : 'loss');
    }
    fireNarration(moveAnnouncer.describeGameOver({ kind: 'win', winner: winnerColor }));
  }

  function buildNetworkController(): NetworkGameController {
    const controller = new NetworkGameController({
      connection,
      localSeat: SEATS.indexOf(localColor),
      onRemoteMove: (move) => {
        // If a local animation is still in flight, queue. (Rare for Russian
        // Shashki since one CHECKERS_MOVE = one turn, but keep it safe.)
        if (animation) {
          pendingRemoteMove = move;
          return;
        }
        beginAnimation(move);
      },
      onOverlayChange: () => {
        // Render loop already runs every frame; nothing extra needed.
      },
      onRematchStart: (newColor, variant) => {
        localColor = newColor;
        onlineVariant = variant;
        startNewGame();
      },
      getSnapshot: () => snapshotState(state),
      applySnapshot: (snapshot) => adoptSnapshot(snapshot),
    });
    controller.setOverlayHandlers({
      onResign: (winnerIsLocal) => {
        resolveResignLocally(winnerIsLocal ? 'opponent' : 'local');
      },
      onRestartStart: () => {
        ActiveGameStore.clear();
        lastEndedByResign = null;
        startNewGame();
        gameOverlay?.close();
      },
      onRestartStateChange: () => {
        if (gameOverlay?.isOpen()) gameOverlay.refreshRestartState();
      },
    });
    controller.setSettingsGetter(() => settingsManager.get());
    return controller;
  }

  // Subscribe globally so the menu transition lands correctly even if the
  // user switches screens while the room is being created/joined.
  roomClient.onUpdate((info) => {
    if (info.state !== 'ready' || info.localSeat === null) return;
    if (currentScene === 'game') return; // already in-game
    localColor = resolveSeatColor(info.seats, info.localSeat);
    onlineVariant = parseVariant(info.variant ?? undefined);
    if (network) network.detach();
    network = buildNetworkController();
    const opponent = info.players?.find((p) => p.seat !== info.localSeat);
    network.setOpponent(
      opponent?.name ?? 'Opponent',
      opponent?.verified ?? false,
      opponent?.avatarUrl ?? null,
      opponent?.userId ?? null,
    );
    network.attach();
    navigateTo('game');
  });

  roomClient.onReconnectFail(() => {
    // RoomClient's own RECONNECT_FAIL handling already clears this room's
    // stored session (clearFor) — nothing further to clear here, and
    // clearing unconditionally would wipe a different room this client
    // still holds a session for.
  });

  function isGameModalActive(): boolean {
    return (
      gameStateOverlay !== null &&
      (network !== null || (opponent === 'ai' && status.kind !== 'playing'))
    );
  }

  new InputHandler({
    canvas,
    getRotation: () => rotationFor(),
    onInteraction: (event) => {
      pokeAfk();
      // Gear-icon hit-test runs first so the overlay is reachable on top of
      // both the online overlay and an idle board.
      // The gear is drawn in screen space and these are raw canvas coordinates,
      // so no un-rotation: flipping them put the click area in the opposite
      // corner from the icon when playing from the far side.
      const overGear = gearBounds !== null && gearHitTest({ x: event.canvasX, y: event.canvasY }, gearBounds);
      if (event.kind === 'move') gearHovered = overGear;
      else if (event.kind === 'leave' || event.kind === 'cancel') gearHovered = false;
      gearTooltip.set(gearHovered ? t('gameOverlay.openTooltip') : null);
      if (event.kind === 'up' && overGear) {
        gearHovered = false;
        gearTooltip.set(null);
        toggleGameOverlay();
        return;
      }
      // Online states + vs-AI game-over surface their UI through the DOM
      // game-state modal (createGameStateOverlay). Swallow pointer events
      // that would otherwise disturb the selector under it; clicks on modal
      // buttons fire directly via the modal handle.
      if (isGameModalActive()) return;
      if (event.kind === 'move') hoveredFallback = event.square;
      if (event.kind === 'leave' || event.kind === 'cancel') hoveredFallback = null;
      selector.handle(event);
    },
  });

  function synthesizeInteraction(
    kind: 'down' | 'move' | 'up',
    x: number,
    y: number,
  ): void {
    if (isGameModalActive()) return;
    const square = canvasToSquare(x, y, rotationFor());
    if (kind === 'move') hoveredFallback = square;
    selector.handle({ kind, canvasX: x, canvasY: y, square, pointer: 'mouse' });
  }

  function computeCursorHint(square: Square): CursorHint {
    const phase = selector.turnState.phase;
    if (phase !== 'selected' && phase !== 'chaining') return 'none';
    const destinations = selector.turnState.nextDestinations();
    if (!destinations.some((d) => sameSquare(d, square))) return 'none';
    const captures = selector.turnState.captureTargetsFor(square);
    return captures.length > 0 ? 'validCapture' : 'validMove';
  }

  function narrateCursor(square: Square): void {
    const piece = state.pieceAt(square);
    const occupant = piece ? { color: piece.color, kind: piece.kind } : null;
    const hint = computeCursorHint(square);
    announce(moveAnnouncer.describeCursor(square, occupant, hint).text);
  }

  const keyboardPlay = new KeyboardPlay({
    hoverAt: (x, y) => {
      pokeAfk();
      synthesizeInteraction('move', x, y);
    },
    clickAt: (x, y) => {
      pokeAfk();
      // Snapshot selector state so we can narrate selection / deselection /
      // chain-step transitions for the SR. Commits get their narration via
      // finishAnimation → describeMove + describeTurnStart instead.
      const ts = selector.turnState;
      const phaseBefore = ts.phase;
      const originBefore = ts.origin;
      const capturesBefore = ts.appliedCaptures.length;
      const stateBefore = state;
      synthesizeInteraction('down', x, y);
      synthesizeInteraction('up', x, y);
      // A move just committed — finishAnimation already announced. Skip.
      if (state !== stateBefore) return;
      const phaseAfter = ts.phase;
      const originAfter = ts.origin;
      const capturesAfter = ts.appliedCaptures.length;
      // Selection: idle → selected, or origin changed within `selected` phase.
      if (
        phaseAfter === 'selected' &&
        originAfter !== null &&
        (phaseBefore !== 'selected' ||
          originBefore === null ||
          !sameSquare(originBefore, originAfter))
      ) {
        const piece = state.pieceAt(originAfter);
        if (piece) {
          announce(moveAnnouncer.describeSelection(originAfter, piece.kind).text);
        }
        return;
      }
      // Deselection.
      if (phaseAfter === 'idle' && phaseBefore === 'selected') {
        announce(moveAnnouncer.describeDeselect().text);
        return;
      }
      // Chain-step progress: a capture was consumed but more to come.
      if (capturesAfter > capturesBefore && phaseAfter === 'chaining') {
        const path = ts.appliedPath;
        const caps = ts.appliedCaptures;
        const lastCap = caps[caps.length - 1];
        const lastLand = path[path.length - 1];
        if (lastCap && lastLand) {
          announce(moveAnnouncer.describeChainStep(lastCap, lastLand).text);
        }
      }
    },
    cancelSelection: () => {
      selector.handle({ kind: 'cancel', canvasX: 0, canvasY: 0, square: null, pointer: 'mouse' });
    },
    requestRender: () => {
      // The rAF loop already drives rendering every frame; no-op.
    },
    getRotation: () => rotationFor(),
    onCursorMove: (square) => narrateCursor(square),
  });

  function handleCanvasKeyDown(event: KeyboardEvent): void {
    if (currentScene !== 'game') return;
    if (gameOverlay?.isOpen()) return;
    if (isGameModalActive()) return;
    if (keyboardPlay.handleKey(event.key)) {
      event.preventDefault();
    }
  }

  canvas.addEventListener('focus', () => {
    keyboardPlay.setFocused(true);
    // Re-announce the cursor square so the user lands oriented after Tab.
    narrateCursor(keyboardPlay.getCursorSquare());
  });
  canvas.addEventListener('blur', () => keyboardPlay.setFocused(false));
  canvas.addEventListener('keydown', handleCanvasKeyDown);

  function handleOverlayButton(id: GameStateOverlayButtonId): void {
    if (!network) {
      // vs-AI game-over overlay: only rematch + return-to-menu are surfaced.
      switch (id) {
        case 'rematch':
          // Swap sides and start a fresh game on the same canvas.
          localColor = opponentOf(localColor);
          startNewGame();
          return;
        case 'return-to-menu':
        case 'leave':
          navigateTo('play');
          return;
        default:
          return;
      }
    }
    switch (id) {
      case 'rematch':
        network.requestRematch();
        return;
      case 'accept':
        // Server echoes REMATCH_START to both players after REMATCH_ACCEPT;
        // onRematchStart handles the local restart with the new color.
        network.acceptRematch();
        return;
      case 'decline':
        network.declineRematch();
        navigateTo('menu');
        return;
      case 'cancel':
        network.dismissOverlay();
        return;
      case 'leave':
      case 'return-to-menu':
        navigateTo('menu');
        return;
    }
  }

  const menuRoot = document.getElementById('menu-root');
  if (menuRoot === null) throw new Error('#menu-root not found');


  const ctxMenu: MenuContext = {
    navigate: (scene) => navigateTo(scene),
    getSettings: () => settingsManager.get(),
    updateSettings: (partial) => {
      const before = settingsManager.get();
      settingsManager.update(partial);
      const after = settingsManager.get();
      if (after.theme !== before.theme) {
        applyTheme(after.theme);
        // Canvas reads `palette` by closure; the rAF loop picks up the new
        // value on the next frame once the game scene becomes active.
        palette = themeToRenderPalette(THEMES[after.theme]);
      }
      if (after.sound !== before.sound) setMuted(!after.sound);
      if (after.language !== before.language) {
        invalidateGameStateLabels();
        void setLocale(after.language).then(() => {
          canvas.setAttribute('aria-label', t('a11y.canvasLabel'));
          router.remountCurrent();
        });
      }
    },
    getOpponent: () => opponent,
    setOpponent: (next) => {
      opponent = next;
    },
    getPendingOnlineColor: () => pendingOnlineColor,
    setPendingOnlineColor: (next) => {
      pendingOnlineColor = next;
    },
    consumePendingJoinCode: () => {
      const code = pendingJoinCode;
      pendingJoinCode = null;
      return code;
    },
    roomClient,
    createRoom: () => {
      roomClient.createRoom(
        settingsManager.get().playerName,
        settingsManager.get().variant,
        SEATS.indexOf(pendingOnlineColor),
      );
    },
    joinRoom: (code) => {
      roomClient.joinRoom(code, settingsManager.get().playerName);
    },
    cancelRoom: () => {
      roomClient.cancel();
      onlineVariant = null;
      if (network) {
        network.detach();
        network = null;
      }
    },
    resumeSession: () => {
      const saved = sessionStore.load();
      if (!saved) return;
      attemptResume(saved.roomCode, recorder.getSession() ?? ActiveGameStore.peek());
    },
    getSavedSession: () => sessionStore.load(),
    getActiveSession: () => recorder.getSession() ?? ActiveGameStore.peek(),
    resumeActiveGame: () => resumeActiveGame(),
    discardActiveSession: (onConfirm) => discardActiveSession(onConfirm),
    historyStore,
    openReplay: (record, returnTo) => {
      pendingReplay = { record, returnTo };
      navigateTo('replay');
    },
  };

  const router = new MenuRouter(menuRoot, ctxMenu);

  function resolveDefaultColor(): Color {
    const pref = settingsManager.get().defaultColor;
    if (pref === 'white' || pref === 'black') return pref;
    return Math.random() < 0.5 ? 'white' : 'black';
  }

  function quickStartVsAi(): void {
    const launch = (): void => {
      opponent = 'ai';
      localColor = resolveDefaultColor();
      navigateTo('game');
    };
    ctxMenu.discardActiveSession(launch);
  }

  router.register('menu', () =>
    createMainMenuScreen({
      version: __APP_VERSION__,
      onQuickStart: () => quickStartVsAi(),
    }),
  );
  router.register('play', createPlayMenuScreen);
  router.register('settings', createSettingsScreen);
  router.register('rules', createRulesScreen);
  router.register('about', createAboutScreen);
  router.register('online-create', createOnlineCreateScreen);
  router.register('online-join', createOnlineJoinScreen);
  router.register('history', createHistoryListScreen);

  function navigateTo(scene: Scene): void {
    const enteringGame = scene === 'game' && currentScene !== 'game';
    const leavingGame = scene !== 'game' && currentScene === 'game';
    const enteringReplay = scene === 'replay' && currentScene !== 'replay';
    const leavingReplay = scene !== 'replay' && currentScene === 'replay';
    const leavingOnlineFlow =
      opponent === 'online' &&
      (currentScene === 'online-create' || currentScene === 'online-join') &&
      scene !== 'game' &&
      scene !== 'online-create' &&
      scene !== 'online-join';

    currentScene = scene;
    socialDock.showScene(scene);
    const mode = modeForScene(scene);
    document.body.setAttribute('data-mode', mode);
    if (leavingGame) aiPlayer.cancel();
    // The active session is intentionally NOT discarded here — it stays in
    // localStorage so the Play menu can offer Resume next time. Online games
    // are an exception: they can't be resumed from ActiveGameStore alone (no
    // session token in the seed), so we drop the slot when leaving them.
    if (leavingGame && opponent === 'online' && recorder.getSession()) {
      recorder.discard();
    }
    if (leavingReplay) replayScene.close();
    if (leavingOnlineFlow) {
      roomClient.cancel();
      if (network) {
        network.detach();
        network = null;
      }
      opponent = 'ai';
      onlineVariant = null;
    }
    if (leavingGame && opponent === 'online') {
      roomClient.cancel();
      if (network) {
        network.detach();
        network = null;
      }
      opponent = 'ai';
      onlineVariant = null;
    }
    if (leavingGame) {
      unmountGameSceneOverlay();
    }
    if (mode === 'menu') {
      router.show(scene);
    } else {
      router.hide();
      if (scene === 'game') {
        mountGameSceneOverlay();
      }
      if (enteringGame) {
        // resumeActiveGame() sets `resumingActiveGame` true before calling
        // navigateTo, so we keep the state it just restored.
        if (!resumingActiveGame) {
          if (opponent === 'ai') {
            // Quick Start pre-sets localColor before navigateTo; PlayMenu's
            // vs-AI button doesn't, so we fall back to settings.defaultColor.
            // The "fresh resolve" happens on every entry to game from non-game.
            localColor = resolveDefaultColor();
          } else if (opponent === 'local') {
            localColor = 'white';
          }
          // online: localColor was already set by the room update handler.
          startNewGame();
        }
        resumingActiveGame = false;
      } else if (enteringReplay && pendingReplay) {
        const { record, returnTo } = pendingReplay;
        pendingReplay = null;
        replayScene.open(record, returnTo);
      }
    }
  }

  window.addEventListener('keydown', (e) => {
    // Game-scene Escape is handled by the in-game overlay's capture-phase
    // listener (toggles the gear modal). Only fall through here for replay.
    if (e.key === 'Escape' && currentScene === 'replay') {
      replayScene.exit();
    }
  });

  // Fires on both sign-in and sign-out. Retrying the outbox is a no-op when
  // the transition was a sign-out (flushOutboxIfSignedIn checks authState);
  // remountCurrent() is a no-op unless a menu screen is showing (it reads
  // `router`'s own current-scene, not `currentScene` above), so this is safe
  // to fire unconditionally rather than gating it on the History screen.
  window.addEventListener(BN_AUTH_CHANGE, () => {
    flushOutboxIfSignedIn();
    router.remountCurrent();
  });

  navigateTo('menu');

  function frame(timeMs: number): void {
    if (currentScene === 'game') {
      render(timeMs);
    } else if (currentScene === 'replay') {
      replayScene.render(ctx, palette, timeMs);
    }
    requestAnimationFrame(frame);
  }

  function render(timeMs: number): void {
    const rotation = rotationFor();
    drawBoard(ctx, palette, rotation);

    let animFrame: MoveAnimationFrame | null = null;
    if (animation) {
      animFrame = frameAt(animation.plan, timeMs - animation.startMs, rotation);
      if (animFrame.done) {
        finishAnimation(animation);
        animFrame = null;
      }
    }

    const renderState = animation
      ? animation.preMoveState
      : selector.turnState.phase === 'chaining' || selector.turnState.phase === 'selected'
        ? selector.turnState.previewState()
        : state;

    const selectorSnapshot = selector.snapshot(timeMs);
    const skipSquares = new Set<string>();
    if (animation) {
      skipSquares.add(squareKey(animation.move.from));
      if (animFrame) {
        for (const cap of animFrame.captures) {
          if (cap.alpha > 0) skipSquares.add(squareKey(cap.square));
        }
      }
    }
    if (selectorSnapshot.dragPosition && selectorSnapshot.selected) {
      skipSquares.add(squareKey(selectorSnapshot.selected));
    }

    drawPieces(ctx, renderState, palette, rotation, (sq) => skipSquares.has(squareKey(sq)));

    if (animation && animFrame) {
      for (const cap of animFrame.captures) {
        if (cap.alpha <= 0) continue;
        const piece = animation.preMoveState.pieceAt(cap.square);
        if (!piece) continue;
        const { x, y } = squareToCanvasCenter(cap.square, rotation);
        drawPiece(ctx, x, y, piece.color, piece.kind, palette, { alpha: cap.alpha });
      }
      drawPiece(
        ctx,
        animFrame.mover.x,
        animFrame.mover.y,
        animation.color,
        animFrame.mover.kind,
        palette,
        { scale: animFrame.mover.scale, shadowLift: animation.move.captures.length > 0 ? 4 : 0 },
      );
      if (animFrame.promotionFlash > 0) {
        drawPromotionFlash(animFrame, animation.color);
      }
    }

    drawTurnBanner(ctx, bannerFor(status), palette);

    // AFK hint pulse — soft ring at the suggested source square. Reduced
    // motion: drop the sine pulse and draw a static high-alpha ring so the
    // hint is still visible.
    if (afkHintSquare !== null && Date.now() < afkHintExpiresAt) {
      const { x, y } = squareToCanvasCenter(afkHintSquare, rotation);
      let alpha = 0.75;
      if (!prefersReducedMotion()) {
        const tt = (Date.now() % 1200) / 1200;
        alpha = Math.max(0.2, Math.min(0.85, 0.35 + 0.4 * Math.sin(tt * Math.PI * 2)));
      }
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, 30, 0, Math.PI * 2);
      ctx.strokeStyle = palette.bannerBorder;
      ctx.globalAlpha = alpha;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.restore();
    }

    // Gear icon — top-right corner, drawn after the rotated board so its
    // coords are screen-space.
    gearBounds = drawPauseGear(ctx, CANVAS_SIZE, {
      fill: palette.bannerBg,
      // The frame-label ink, not the banner's hairline border: that one is ~1.4:1 against
      // the dark surround, and an icon needs 3:1 (WCAG 1.4.11).
      border: palette.labelColor,
    }, { hover: gearHovered });

    const overlayState = network ? network.overlay : 'none';
    if (overlayState === 'none' && !animation) {
      drawInteractionOverlay(ctx, selectorSnapshot, palette, rotation);
    } else if (overlayState === 'none' && hoveredFallback) {
      drawInteractionOverlay(
        ctx,
        { ...selectorSnapshot, hovered: hoveredFallback, validDestinations: [], captureTargets: [] },
        palette,
        rotation,
      );
    }

    if (keyboardPlay.isFocused() && overlayState === 'none') {
      drawKeyboardFocusRing(ctx, keyboardPlay.getCursorSquare(), palette, rotation);
    }

    syncGameStateOverlay();
  }

  /**
   * Drive the DOM game-state modal from the current network + game status.
   * Idempotent — safe to call every render; the helper only touches DOM on
   * actual changes, so the active focus inside the modal stays stable.
   */
  function syncGameStateOverlay(): void {
    const snapshot = computeGameStateSnapshot();
    if (snapshot === null) {
      gameStateOverlay?.close();
      gameStateOverlay = null;
      return;
    }
    if (gameStateOverlay === null) {
      gameStateOverlay = createGameStateOverlay(getGameStateLabels, {
        onAction: (id) => handleOverlayButton(id),
      });
    }
    gameStateOverlay.update(snapshot);
  }

  function computeGameStateSnapshot(): GameStateOverlaySnapshot | null {
    if (network && network.overlay !== 'none') {
      const opponentName = network.opponentVerified
        ? t('online.identity.verifiedName', { name: network.opponentName })
        : network.opponentName;
      return {
        state: network.overlay,
        opponentName,
        opponentAvatarUrl: network.opponentAvatarUrl,
        opponentUserId: network.opponentUserId,
        gracePeriodEndsAt: network.gracePeriodEndsAt,
        ...(network.gameOverResult !== null && network.gameOverResult !== undefined
          ? { gameOverResult: network.gameOverResult }
          : {}),
      };
    }
    if (!network && opponent === 'ai' && status.kind !== 'playing' && !animation) {
      const result: 'win' | 'loss' | 'draw' =
        status.kind === 'draw' ? 'draw' : status.winner === localColor ? 'win' : 'loss';
      return {
        state: 'game_over',
        opponentName: '',
        gracePeriodEndsAt: null,
        gameOverResult: result,
      };
    }
    return null;
  }

  function drawPromotionFlash(animFrame: MoveAnimationFrame, color: Color): void {
    const alpha = animFrame.promotionFlash;
    if (alpha <= 0) return;
    ctx.save();
    ctx.beginPath();
    ctx.arc(animFrame.mover.x, animFrame.mover.y, 32, 0, Math.PI * 2);
    ctx.strokeStyle = color === 'white' ? palette.whiteCrownGold : palette.blackCrownGold;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 3;
    ctx.shadowColor = ctx.strokeStyle;
    ctx.shadowBlur = 14;
    ctx.stroke();
    ctx.restore();
  }

  // URL hash takes precedence over a stored session. A #room=CODE link now
  // always resumes directly — the server resolves the seat (reclaimed
  // silently if it's ours, or a fresh ROOM_JOINED if it's genuinely new) —
  // rather than dropping the user on the Join screen.
  function resumeFromHashOrSession(): void {
    // No ActiveSession has necessarily been recorded yet at boot time — fall
    // back the same way ctxMenu.getActiveSession() does, so the failure
    // modal's View saved/Discard actions appear whenever there's something
    // to act on and are silently omitted otherwise.
    const session = recorder.getSession() ?? ActiveGameStore.peek();

    const hashEntry = readRoomHash();
    if (hashEntry) {
      // Seed a legacy token (if the link predates RESUME_ROOM) into the
      // room-keyed store so resume() can offer it to resolveSeat as one more
      // candidate — never as a trusted identity on its own.
      if (hashEntry.legacyToken) {
        sessionStore.saveFor(hashEntry.roomCode, hashEntry.legacyToken);
      }
      opponent = 'online';
      attemptResume(hashEntry.roomCode, session);
      return;
    }
    // Try to resume a stored online session on app load — if a tab refreshed
    // mid-game within the grace window, jump straight back to play.
    const storedEntry = sessionStore.load();
    if (storedEntry) {
      opponent = 'online';
      attemptResume(storedEntry.roomCode, session);
    }
  }

  /**
   * Everyone who launched the activity in the same channel shares one Discord
   * instance id, so it doubles as the room key — no lobby codes to exchange.
   * The Discord roster, not the stored setting, names the player here; the
   * setting is left untouched so the web build keeps the user's own choice.
   * Resolves false when the handshake failed, so startup can fall through to
   * the normal menu instead of dead-ending.
   */
  async function startDiscordActivity(): Promise<boolean> {
    const discord = await initDiscordActivity();
    if (discord === null) return false;

    connection.setTicket(discord.ticket);
    opponent = 'online';
    // The room becomes playable through the shared roomClient.onUpdate handler.
    roomClient.joinOrCreate(discord.instanceId, {
      playerName: discord.displayName,
      variant: settingsManager.get().variant,
    });
    return true;
  }

  requestAnimationFrame(frame);

  // Inside Discord the instance room is authoritative — a saved session or a
  // share-link hash must not race it. Only one of the two paths ever runs.
  if (isDiscordActivity()) {
    void startDiscordActivity().then((joined) => {
      if (!joined) resumeFromHashOrSession();
    });
  } else {
    resumeFromHashOrSession();
  }
}

function squareKey(sq: Square): string {
  return `${sq.row},${sq.col}`;
}

function applyTheme(theme: Settings['theme']): void {
  document.body.setAttribute('data-theme', theme);
}

function applyLayoutAttributes(config: LayoutConfig): void {
  document.body.setAttribute('data-layout', config.mode);
  document.body.setAttribute('data-orientation', config.orientation);
}

void main();
